<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import axios from 'axios'
import { debounce } from 'lodash'
import { notify } from '@kyvg/vue3-notification'
import { InputText, Select } from 'primevue'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'
import { routeType } from '@/types/route'

type Option = Record<string, any>

const model = defineModel<any>()

const props = withDefaults(defineProps<{
    fetchRoute: routeType
    optionLabel?: string
    optionValue?: string
    placeholder?: string
    searchPlaceholder?: string
    emptyText?: string
    initialOption?: Option | null
    itemSize?: number
    showClear?: boolean
    disabled?: boolean
    invalid?: boolean
    size?: 'small' | 'large'
    fluid?: boolean
}>(), {
    optionLabel: 'name',
    optionValue: 'id',
    placeholder: 'Pilih',
    searchPlaceholder: 'Cari...',
    emptyText: 'Tidak ada data',
    initialOption: null,
    itemSize: 38,
    fluid: true,
})

const emits = defineEmits<{
    (e: 'change', option: Option | null): void
}>()

const options = ref<Option[]>(props.initialOption ? [props.initialOption] : [])
const nextUrl = ref<string | null>(null)
const hasLoadedOnce = ref(false)
const isLoading = ref(false)
const search = ref('')
let latestRequestId = 0

const buildUrl = (query: string): string => route(props.fetchRoute.name, {
    ...(props.fetchRoute.parameters ?? {}),
    ...(query ? { 'filter[global]': query } : {}),
})

const mergeOptions = (current: Option[], incoming: Option[]): Option[] => {
    const seen = new Set(current.map(option => option[props.optionValue]))

    return [...current, ...incoming.filter(option => !seen.has(option[props.optionValue]))]
}

const fetchPage = async (url: string, replace: boolean) => {
    const requestId = ++latestRequestId
    isLoading.value = true

    try {
        const response = await axios.get(url)
        if (requestId !== latestRequestId) return

        const incoming: Option[] = response.data?.data ?? []
        options.value = replace ? keepSelected(incoming) : mergeOptions(options.value, incoming)
        nextUrl.value = response.data?.links?.next ?? null
        hasLoadedOnce.value = true
    } catch (error) {
        if (requestId !== latestRequestId) return
        notify({ title: 'Gagal memuat data', text: 'Coba buka lagi pilihannya.', type: 'error' })
    } finally {
        if (requestId === latestRequestId) isLoading.value = false
    }
}

// The selected option stays in the list so its label keeps showing while searching.
const keepSelected = (incoming: Option[]): Option[] => {
    const selected = selectedOption.value
    if (!selected) return incoming

    return mergeOptions([selected], incoming)
}

const selectedOption = computed<Option | null>(() =>
    options.value.find(option => option[props.optionValue] === model.value) ?? null
)

const loadFirstPage = () => fetchPage(buildUrl(search.value), true)

const onShow = () => {
    if (!hasLoadedOnce.value) loadFirstPage()
}

const onSearch = debounce(() => loadFirstPage(), 400)

watch(search, () => {
    isLoading.value = true
    onSearch()
})

const onLazyLoad = (event: { first: number, last: number }) => {
    if (!nextUrl.value || isLoading.value) return
    if (event.last >= options.value.length - 5) fetchPage(nextUrl.value, false)
}

const onChange = (value: any) => {
    model.value = value
    emits('change', options.value.find(option => option[props.optionValue] === value) ?? null)
}

watch(() => props.initialOption?.[props.optionValue], () => {
    if (props.initialOption) options.value = mergeOptions([props.initialOption], options.value)
})

watch(() => `${props.fetchRoute.name}|${JSON.stringify(props.fetchRoute.parameters ?? {})}`, () => {
    options.value = props.initialOption ? [props.initialOption] : []
    nextUrl.value = null
    hasLoadedOnce.value = false
})

const searchInput = ref<any>(null)
const focusSearch = () => nextTick(() => (searchInput.value?.$el as HTMLInputElement | undefined)?.focus())

defineExpose({ reload: loadFirstPage })
</script>

<template>
    <Select
        :modelValue="model"
        :options="options"
        :optionLabel="optionLabel"
        :optionValue="optionValue"
        :placeholder="placeholder"
        :showClear="showClear"
        :disabled="disabled"
        :invalid="invalid"
        :size="size"
        :fluid="fluid"
        :loading="isLoading && !options.length"
        :virtualScrollerOptions="{
            lazy: true,
            onLazyLoad,
            itemSize,
            showLoader: isLoading && hasLoadedOnce,
            loading: false,
        }"
        @show="() => { onShow(); focusSearch() }"
        @update:modelValue="onChange"
    >
        <template #header>
            <div class="border-b border-gray-100 p-2">
                <InputText
                    ref="searchInput"
                    v-model="search"
                    :placeholder="searchPlaceholder"
                    size="small"
                    fluid
                    @keydown.stop
                />
            </div>
        </template>

        <template #value="slotProps">
            <slot name="value" v-bind="slotProps" :selectedOption="selectedOption">
                <span v-if="selectedOption">{{ selectedOption[optionLabel] }}</span>
                <span v-else>{{ slotProps.placeholder }}</span>
            </slot>
        </template>

        <template #option="slotProps">
            <slot name="option" v-bind="slotProps">
                {{ slotProps.option[optionLabel] }}
            </slot>
        </template>

        <template #empty>
            <div class="flex items-center gap-x-2 px-3 py-2 text-sm text-gray-400">
                <template v-if="isLoading"><LoadingIcon /> Memuat...</template>
                <template v-else>{{ emptyText }}</template>
            </div>
        </template>

        <template #footer>
            <div v-if="isLoading && options.length" class="flex items-center justify-center gap-x-2 border-t border-gray-100 py-1.5 text-xs text-gray-400">
                <LoadingIcon /> Memuat lagi...
            </div>
        </template>
    </Select>
</template>
